import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@apollo/client';
import { Card, CardContent, CardFooter, Typography } from '@guildeducationinc/recess';
import Illustration from '@src/assets/submit-program-illustration.png';
import { Alert } from '@src/components/Alert/Alert';
import { ProgramDetails } from '@src/components/Employee/PaymentRequests/EditPaymentRequest/ProgramDetails';
import { RequestSteps } from '@src/components/Employee/RequestSteps';
import { FullPageWrapper } from '@src/components/FullPageWrapper';
import { TopBar } from '@src/components/TopBar';
import { PAYMENT_REQUEST } from '@src/graphql/queries/paymentRequest';
import { useAuth } from '@src/hooks/useAuth';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { usePrograms } from '@src/hooks/usePrograms';
import { PaymentMethod, ProgramStatus, RuleType, } from '@src/types';
import { warnOnOtherFieldOfStudy, warnOnOtherSchool } from '@src/utilities/benefitsHelper';
import { checkEligibilityLock } from '@src/utilities/paymentRequestHelper';
import { TrackableButton, useBreakpoints, useLocale } from 'pe-core-frontend';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import styles from './SubmitConfirmation.module.scss';
export const SubmitConfirmation = ({ isPaymentRequest }) => {
    const { t } = useLocale();
    const { id } = useParams();
    const idNumber = parseInt(id || '0', 10);
    const { find: findProgram } = usePrograms({ excludeSingleExpenses: false });
    const { data: paymentRequestData } = useQuery(PAYMENT_REQUEST, {
        variables: { id: idNumber },
        skip: !isPaymentRequest,
    });
    const { currentUser } = useAuth();
    const paymentRequest = paymentRequestData?.paymentRequest;
    const programId = isPaymentRequest ? paymentRequest?.programId : idNumber;
    const program = (programId && findProgram(programId)) || undefined;
    const { myBenefits, loading: benefitsLoading, getPolicy } = useMyBenefits({ program });
    const otherOptionRequiresReview = !isPaymentRequest &&
        !benefitsLoading &&
        ((program?.otherSchool && warnOnOtherSchool(myBenefits, program)) ||
            (program?.otherFieldOfStudy && warnOnOtherFieldOfStudy(myBenefits, program)));
    const { isMobile } = useBreakpoints();
    const approvalChainPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    const approvalDeadline = approvalChainPolicy?.paymentRequestApproval?.deadline || {};
    const eligibilityLock = useMemo(() => {
        if (!paymentRequest && !program) {
            return {
                locked: false,
                canSubmitExpenses: false,
                expensesRequired: false,
                preimbursement: false,
            };
        }
        const eligibilityPolicy = getPolicy(RuleType.ELIGIBILITY, program);
        if (!paymentRequest) {
            return {
                locked: eligibilityPolicy?.enableEligibilityLock || false,
                canSubmitExpenses: eligibilityPolicy?.preimbursement ||
                    approvalChainPolicy?.paymentRequestApproval?.enableWorkflow ||
                    false,
                expensesRequired: true,
                preimbursement: eligibilityPolicy?.preimbursement || false,
            };
        }
        const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
        return checkEligibilityLock(paymentRequest, program, eligibilityPolicy, approvalPolicy);
    }, [getPolicy, program, paymentRequest]);
    const expensesRequired = !eligibilityLock.locked || eligibilityLock.expensesRequired;
    const promptForNextTerm = paymentRequest && eligibilityLock.locked && eligibilityLock.expensesRequired;
    const programNeedsReview = [
        ProgramStatus.PENDING,
        ProgramStatus.IN_REVIEW,
        ProgramStatus.INFO_REQUESTED,
    ].includes(program?.status || ProgramStatus.PENDING);
    const getTitle = () => {
        if (isPaymentRequest) {
            if (paymentRequest?.paymentMethod === PaymentMethod.PREIMBURSEMENT) {
                if (paymentRequest?.preimbursementStatus) {
                    return t('Your proof of payment and completion are in.');
                }
                else {
                    return t('Your advance payment request is in.');
                }
            }
            if (!expensesRequired) {
                return t('You can submit expenses for this term!');
            }
            return t('You did it! Your request is in.');
        }
        if (programNeedsReview) {
            return t('You did it! Your application is in.');
        }
        if (!expensesRequired) {
            return t("Let's confirm you are eligible for the current term or billing period.");
        }
        return t('You can now submit expenses for your {progLevel}!', {
            values: { progLevel: program?.programLevelName },
        });
    };
    const getDescription = () => {
        if (isPaymentRequest) {
            if (!expensesRequired) {
                return t('Payment request eligible description', {
                    values: {
                        link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'submit_confirmation', to: '/reimbursement-policy', children: text })),
                    },
                });
            }
            if (paymentRequest?.paymentMethod === PaymentMethod.PREIMBURSEMENT) {
                if (paymentRequest?.preimbursementStatus) {
                    return t('Thank you for submitting your proof of payment and completion. Our team is currently reviewing your documents.');
                }
            }
            return t('Your request has been successfully submitted');
        }
        if (programNeedsReview) {
            return t('Your program application has been successfully submitted.');
        }
        if (!expensesRequired) {
            return t("Thank you for sharing information about this {progLevel}! You must meet {employerName}'s benefit requirements at the start of each term. Please tell us about the current term so we can confirm your eligibility.", {
                values: {
                    progLevel: program?.programLevelName,
                    // confirmation is only shown for the user who submitted the request, so it is safe to use currentUser's info
                    employerName: currentUser?.clientInfo?.name,
                    link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'submit_confirmation', to: '/reimbursement-policy', children: text })),
                },
            });
        }
        return t('Program submitted description', {
            values: {
                link: (text) => (_jsx(TrackableButton, { btnType: 'link', component: Link, enText: 'Employer policy', tag: 'submit_confirmation', to: '/reimbursement-policy', children: text })),
            },
        });
    };
    const showSubmitExpenses = promptForNextTerm || (!isPaymentRequest && !programNeedsReview);
    return (_jsxs(FullPageWrapper, { children: [_jsx(TopBar, { backLink: '/' }), _jsx("div", { className: styles.container, children: _jsx("div", { style: { backgroundImage: `url(${Illustration})` }, className: styles.illustration, children: _jsxs(Card, { className: styles.card, children: [_jsxs(CardContent, { className: styles.cardContent, children: [_jsx(Typography, { variant: 'title2', weight: '300', children: getTitle() }), _jsx(Typography, { variant: 'body1', children: getDescription() }), program && !program.isSingleExpense && (_jsx(ProgramDetails, { program: program, showCompletionDate: true, showIcon: true, termDates: paymentRequest
                                            ? [paymentRequest.termStartDate, paymentRequest.termEndDate]
                                            : undefined })), _jsx(Typography, { variant: 'body1', weight: '700', children: t('Confirmation ID: {requestNum}', {
                                            values: { requestNum: paymentRequest?.requestNum || program?.requestNum },
                                        }) }), program && (_jsx(RequestSteps, { showHeading: true, program: program, className: styles.steps, paymentRequest: paymentRequest, hideNotification: true })), otherOptionRequiresReview && (_jsx(Alert, { type: 'warning', message: _jsxs("div", { className: 'flex flex-col gap-1', children: [_jsx(Typography, { variant: 'label2', weight: '700', children: t('Program requires additional review') }), program?.otherSchool && warnOnOtherSchool(myBenefits, program) && (_jsx(Typography, { variant: 'label2', children: t('Other school warning') })), program?.otherFieldOfStudy &&
                                                    warnOnOtherFieldOfStudy(myBenefits, program) && (_jsx(Typography, { variant: 'label2', children: t('Other field of study warning') }))] }) })), promptForNextTerm && (
                                    // Show the below section only if the user is eligible to submit expenses
                                    _jsxs("div", { className: 'mt-2 flex flex-col gap-2', children: [_jsx(Typography, { variant: 'body1', weight: '700', children: t('Are you continuing with another term?') }), _jsx(Typography, { variant: 'body1', children: t((approvalDeadline.minInterval?.days ?? 0) === 0
                                                    ? 'Confirm term eligibility description_0_days'
                                                    : 'Confirm term eligibility description', {
                                                    values: { days: approvalDeadline.minInterval?.days ?? 0 },
                                                }) })] }))] }), _jsxs(CardFooter, { className: styles.cardFooter, children: [_jsx(TrackableButton, { enText: 'Go back home', tag: 'card_footer', appearance: !showSubmitExpenses ? 'primary' : 'ghost', Component: Link, to: '/', fullWidth: isMobile, children: t('Go back home') }), showSubmitExpenses && (_jsx(TrackableButton, { enText: promptForNextTerm || !expensesRequired
                                            ? 'Tell us about a new term'
                                            : 'Submit expense', tag: 'card_footer', Component: Link, to: `/payments/new/${programId}`, fullWidth: isMobile, children: promptForNextTerm || !expensesRequired
                                            ? t('Tell us about a new term')
                                            : t('Submit expenses') }))] })] }) }) })] }));
};
